import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, Plus, Link2, Check } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFlash } from "../lib/flash";
import { FB_LABEL, ACCESS_LABEL } from "../components/StoreSwitcher";

function Consequence({ d }) {
  return (
    <div className="notice info stores-cons" data-testid="stores-billing-note">
      <b>Butikken lægges på jeres samlede faktura: +{d.price_monthly} kr./md. ekskl. moms fra næste faktura.</b>
      <span>{d.in_trial ? " I er i prøveperioden – den nye butik deler jeres prøveperiode og har ikke sin egen." : " Der er ingen forholdsmæssig afregning – butikken kommer med som en hel måned på næste faktura."}</span>
    </div>
  );
}

function AddStore({ d, onDone }) {
  const { switchStore } = useAuth();
  const flash = useFlash();
  const [f, setF] = useState({ store_name: "", terms: false, confirm: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post("/account/stores", f);
      flash(r.data.flash);
      await switchStore(r.data.store.id);
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <form className="panel" id="tilfoej" onSubmit={submit} data-testid="stores-add-form">
      <h2><Plus size={18} /> Tilføj butik</h2>
      <p className="muted">Opret en ny butik under dit login. Den får sin egen Facebook-side, egne tilbud, kunder, medarbejdere og statistik.</p>
      <div className="field">
        <label htmlFor="st-name">Butikkens navn</label>
        <input id="st-name" value={f.store_name} maxLength={80} onChange={(e) => setF({ ...f, store_name: e.target.value })} data-testid="stores-add-name" />
      </div>
      <Consequence d={d} />
      <label className="check"><input type="checkbox" checked={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} data-testid="stores-add-confirm" /> Ja, læg butikken på vores samlede faktura</label>
      <label className="check"><input type="checkbox" checked={f.terms} onChange={(e) => setF({ ...f, terms: e.target.checked })} data-testid="stores-add-terms" /> Jeg accepterer <a href="/vilkaar" target="_blank" rel="noreferrer">vilkårene</a> og <a href="/databehandleraftale" target="_blank" rel="noreferrer">databehandleraftalen</a> for den nye butik</label>
      {err && <div className="notice error" data-testid="stores-add-error">{err}</div>}
      <button className="btn primary" disabled={busy || !f.store_name.trim() || !f.confirm || !f.terms} data-testid="stores-add-submit">{busy ? "Opretter…" : "Opret butik"}</button>
    </form>
  );
}

function LinkAccount({ d, onDone }) {
  const flash = useFlash();
  const [f, setF] = useState({ email: "", password: "", confirm: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post("/account/link", f);
      flash(r.data.flash);
      setF({ email: "", password: "", confirm: false });
      onDone();
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="panel" id="tilknyt" onSubmit={submit} autoComplete="off" data-testid="stores-link-form">
      <h2><Link2 size={18} /> Tilknyt eksisterende konto</h2>
      <p className="muted">Har du allerede en anden JaTak Pro-konto? Log ind med den her én gang. Bagefter kan begge e-mails logge ind og se alle butikkerne.</p>
      <div className="sup-row">
        <div className="field">
          <label htmlFor="ln-email">E-mail på den anden konto</label>
          <input id="ln-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} autoComplete="off" data-testid="stores-link-email" />
        </div>
        <div className="field">
          <label htmlFor="ln-pw">Adgangskode</label>
          <input id="ln-pw" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" data-testid="stores-link-password" />
        </div>
      </div>
      <Consequence d={{ ...d, in_trial: false }} />
      <p className="muted" style={{ fontSize: 13 }}>Fakturaer, der allerede er sendt til den anden konto, skal stadig betales og kan ses under Abonnement. Dens egen prøveperiode og fakturadato bortfalder – fremover følger den jeres samlede faktura.</p>
      <label className="check"><input type="checkbox" checked={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} data-testid="stores-link-confirm" /> Ja, læg butikken på vores samlede faktura</label>
      {err && <div className="notice error" data-testid="stores-link-error">{err}</div>}
      <button className="btn primary" disabled={busy || !f.email || !f.password || !f.confirm} data-testid="stores-link-submit">{busy ? "Tilknytter…" : "Tilknyt konto"}</button>
    </form>
  );
}

function RemoveStore({ s, onDone, onCancel }) {
  const { setToken } = useAuth();
  const flash = useFlash();
  const [f, setF] = useState({ email: "", password: "", confirm: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/account/stores/${s.id}/remove`, f);
      flash(r.data.flash);
      if (r.data.token) {
        setToken(r.data.token);
        window.location.assign("/butikker");
        return;
      }
      onDone();
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <form className="stores-remove" onSubmit={submit} data-testid={`stores-remove-form-${s.id}`}>
      <p><b>Fjern «{s.name}» fra gruppen?</b></p>
      <ul className="muted">
        <li>Butikken får sin egen faktura. Der er ingen ny prøveperiode: er I i prøveperioden, beholder butikken samme slutdato. Betaler I, starter butikken sin egen månedlige faktura fra næste periode – uden forholdsmæssig afregning eller refusion.</li>
        <li>Fakturaer, der allerede er sendt, bliver i gruppen.</li>
        <li>{s.has_login ? `Butikkens eget login (${s.login_email}) virker fortsat, men ser kun denne butik.` : "Butikken har ikke sit eget login – vælg e-mail og adgangskode til det nye login herunder."} Andre logins i gruppen mister adgangen til butikken med det samme.</li>
      </ul>
      {!s.has_login && (
        <div className="sup-row">
          <div className="field">
            <label htmlFor={`rm-email-${s.id}`}>E-mail til butikkens login</label>
            <input id={`rm-email-${s.id}`} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} autoComplete="off" data-testid={`stores-remove-email-${s.id}`} />
          </div>
          <div className="field">
            <label htmlFor={`rm-pw-${s.id}`}>Adgangskode (mindst 8 tegn)</label>
            <input id={`rm-pw-${s.id}`} type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="new-password" data-testid={`stores-remove-password-${s.id}`} />
          </div>
        </div>
      )}
      <label className="check"><input type="checkbox" checked={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} data-testid={`stores-remove-confirm-${s.id}`} /> Ja, fjern butikken fra gruppen</label>
      {err && <div className="notice error" data-testid={`stores-remove-error-${s.id}`}>{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn danger small" disabled={busy || !f.confirm} data-testid={`stores-remove-submit-${s.id}`}>{busy ? "Fjerner…" : "Fjern fra gruppen"}</button>
        <button type="button" className="btn small" onClick={onCancel}>Annullér</button>
      </div>
    </form>
  );
}

export default function StoresPage() {
  const { switchStore } = useAuth();
  const [d, setD] = useState(null);
  const [removing, setRemoving] = useState(null);
  const load = () => api.get("/account/stores").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (d && window.location.hash) document.querySelector(window.location.hash)?.scrollIntoView({ block: "start" });
  }, [d]);
  if (!d) return <div className="wrap"><p className="muted">Henter…</p></div>;
  return (
    <div className="wrap stores-page" data-testid="stores-page">
      <h1>Butikker</h1>
      <p className="muted">Alle butikker under dit login samles på én faktura: {d.price_monthly} kr./md. ekskl. moms pr. butik. Facebook, tilbud, kunder, medarbejdere og statistik er altid pr. butik.</p>
      <section className="panel" data-testid="stores-list">
        <h2><Store size={18} /> Dine butikker ({d.stores.length})</h2>
        <ul className="stores-list">
          {d.stores.map((s) => (
            <li key={s.id} data-testid={`stores-row-${s.id}`}>
              <div className="stores-row-main">
                <div>
                  <b>{s.name}</b>{s.primary && d.stores.length > 1 && <small className="pill">Fakturamodtager</small>}
                  <small className="muted"><i className={`ss-dot ${s.fb}`} /> {FB_LABEL[s.fb]}{s.fb_page_name ? ` (${s.fb_page_name})` : ""} · {s.locked ? "Låst" : ACCESS_LABEL[s.access_kind] || s.access_kind}</small>
                </div>
                <div className="stores-row-btns">
                  {s.current ? <span className="pill"><Check size={13} /> Aktiv nu</span> : (
                    <button type="button" className="btn small" onClick={() => switchStore(s.id)} data-testid={`stores-switch-${s.id}`}>Skift hertil</button>
                  )}
                  {d.stores.length > 1 && removing !== s.id && (
                    <button type="button" className="btn small ghost" onClick={() => setRemoving(s.id)} data-testid={`stores-remove-${s.id}`}>Fjern fra gruppen</button>
                  )}
                </div>
              </div>
              {removing === s.id && <RemoveStore s={s} onDone={() => window.location.reload()} onCancel={() => setRemoving(null)} />}
            </li>
          ))}
        </ul>
        <p className="muted" style={{ fontSize: 13 }}>Fakturaoplysninger for hele gruppen redigeres under <Link to="/billing">Abonnement</Link>.</p>
      </section>
      {d.can_add && <AddStore d={d} />}
      {d.can_add && <LinkAccount d={d} onDone={load} />}
    </div>
  );
}
